import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { getLocalDate } from '@/utils/dateUtils';
import { format } from 'date-fns';

interface AppointmentSlotsDialogsComponentProps {
  // Filter dialog state
  isFilterDialogOpen: boolean;
  filterStartDate: Date | undefined;
  filterEndDate: Date | undefined;
  filterStartTime: string;
  filterEndTime: string;

  // Confirmation dialog states
  deleteConfirmDialog: {
    isOpen: boolean;
    appointmentId: number | null;
  };
  deleteAllConfirmDialog: boolean;

  // Filter dialog handlers
  onFilterDialogOpenChange: (open: boolean) => void;
  onFilterStartDateChange: (date: Date | undefined) => void;
  onFilterEndDateChange: (date: Date | undefined) => void;
  onFilterStartTimeChange: (time: string) => void;
  onFilterEndTimeChange: (time: string) => void;
  onFilterConfirm: () => void;

  // Confirmation dialog handlers
  onDeleteConfirmDialogChange: (state: { isOpen: boolean; appointmentId: number | null }) => void;
  onDeleteAllConfirmDialogChange: (open: boolean) => void;
  onConfirmDeleteAppointment: () => void;
  onConfirmDeleteAll: () => void;
}

const AppointmentSlotsDialogsComponent: React.FC<AppointmentSlotsDialogsComponentProps> = ({
  // Filter dialog state
  isFilterDialogOpen,
  filterStartDate,
  filterEndDate,
  filterStartTime,
  filterEndTime,

  // Confirmation dialog states
  deleteConfirmDialog,
  deleteAllConfirmDialog,

  // Filter dialog handlers
  onFilterDialogOpenChange,
  onFilterStartDateChange,
  onFilterEndDateChange,
  onFilterStartTimeChange,
  onFilterEndTimeChange,
  onFilterConfirm,

  // Confirmation dialog handlers
  onDeleteConfirmDialogChange,
  onDeleteAllConfirmDialogChange,
  onConfirmDeleteAppointment,
  onConfirmDeleteAll,
}) => {
  return (
    <>
      {/* Facility Filter Time Slots Dialog */}
      <Dialog open={isFilterDialogOpen} onOpenChange={onFilterDialogOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Filter Time Slots</DialogTitle>
            <DialogDescription>
              Select date and time range to filter appointments
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Start Date</label>
                <Input
                  type="date"
                  value={filterStartDate ? format(filterStartDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => onFilterStartDateChange(e.target.value ? getLocalDate(e.target.value) : undefined)}
                  className="w-full bg-purple-50 text-purple-700 border-purple-200 focus:border-purple-400 focus:ring-purple-400 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  placeholder="Select start date"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">End Date</label>
                <Input
                  type="date"
                  value={filterEndDate ? format(filterEndDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => onFilterEndDateChange(e.target.value ? getLocalDate(e.target.value) : undefined)}
                  className="w-full bg-purple-50 text-purple-700 border-purple-200 focus:border-purple-400 focus:ring-purple-400 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  placeholder="Select end date"
                />
              </div>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Start Time</label>
                <Input
                  type="time"
                  value={filterStartTime}
                  onChange={(e) => onFilterStartTimeChange(e.target.value)}
                  className="w-full bg-purple-50 text-purple-700 border-purple-200 focus:border-purple-400 focus:ring-purple-400 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  placeholder="Select start time"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">End Time</label>
                <Input
                  type="time"
                  value={filterEndTime}
                  onChange={(e) => onFilterEndTimeChange(e.target.value)}
                  className="w-full bg-purple-50 text-purple-700 border-purple-200 focus:border-purple-400 focus:ring-purple-400 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  placeholder="Select end time"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onFilterDialogOpenChange(false)}
              className="px-8 bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
            >
              Cancel
            </Button>
            <Button
              onClick={onFilterConfirm}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Appointment Confirmation Dialog */}
      <AlertDialog
        open={deleteConfirmDialog.isOpen}
        onOpenChange={(open) => onDeleteConfirmDialogChange({ isOpen: open, appointmentId: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the appointment slot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDeleteAppointment}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Appointments Confirmation Dialog */}
      <AlertDialog open={deleteAllConfirmDialog} onOpenChange={onDeleteAllConfirmDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete all filtered appointments?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete all appointment slots that don't have associated patient data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDeleteAll}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AppointmentSlotsDialogsComponent;